import Image from 'next/image'
import * as React from 'react'

import { cn } from '@/lib/utils'

import type { NonFsdsGeneralV1MentorSpotlight } from '@/types/non-fsds-general-v1.type'

interface MentorSpotlightProps {
  mentors: NonFsdsGeneralV1MentorSpotlight[]
  isEnglish: boolean
  className?: string
}

/**
 * Mentor spotlight — single image-led card with overlay text and circular
 * prev/next controls. 5s autoplay, pause on hover/focus, reduced-motion
 * disabled. Deterministic initial state (always slide 0).
 */
export function MentorSpotlight({ mentors, isEnglish: _isEnglish, className }: MentorSpotlightProps) {
  const [activeIndex, setActiveIndex] = React.useState(0)
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const [paused, setPaused] = React.useState(false)

  const count = mentors?.length || 0

  const goNext = React.useCallback(() => {
    if (count <= 1) return
    setActiveIndex(prev => (prev + 1) % count)
  }, [count])

  const goPrev = React.useCallback(() => {
    if (count <= 1) return
    setActiveIndex(prev => (prev - 1 + count) % count)
  }, [count])

  // Autoplay
  React.useEffect(() => {
    if (count <= 1 || paused) return
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    intervalRef.current = setInterval(goNext, 5000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [count, paused, goNext])

  if (!mentors || mentors.length === 0) return null

  const mentor = mentors[activeIndex]

  return (
    <div
      className={cn(
        'relative h-[410px] overflow-hidden rounded-[var(--gv1-radius-card)]',
        className
      )}
      data-testid="gv1-mentor-spotlight"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* Background image */}
      <Image
        key={mentor.id}
        src={mentor.image || '/templates/general-v1/assets/mentor-spotlight.webp'}
        alt={mentor.name}
        fill
        className="object-cover object-top motion-safe:transition-opacity motion-safe:duration-500"
        sizes="(max-width: 768px) 100vw, 50vw"
      />

      {/* Bottom gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Mentor info */}
      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
        <h3
          className="text-xl font-bold text-white"
          style={{ fontFamily: 'var(--gv1-font-heading)' }}
        >
          {mentor.name}
        </h3>
        <p className="mt-1 text-sm font-medium text-white/80">
          {mentor.role}
        </p>
        {mentor.description && (
          <p className="mt-2 line-clamp-2 text-sm text-white/70">
            {mentor.description}
          </p>
        )}
      </div>

      {/* Navigation controls */}
      {count > 1 && (
        <div className="absolute right-6 top-6 flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            className="flex size-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Previous mentor"
          >
            <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goNext}
            className="flex size-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Next mentor"
          >
            <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      )}

      {/* Slide indicator dots */}
      {count > 1 && (
        <div className="absolute bottom-6 right-6 flex gap-1.5">
          {mentors.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveIndex(idx)}
              className={cn(
                'rounded-full transition-all duration-300',
                idx === activeIndex
                  ? 'h-2 w-6 bg-white'
                  : 'size-2 bg-white/40 hover:bg-white/60'
              )}
              aria-label={`Go to mentor ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
